import { type FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useSession } from "@/app/session";
import { Alert, Button, Field, Tabs } from "@/components/ui";
import { authClient } from "@/lib/authClient";
import { errText, useT } from "@/lib/i18n";
import { AuthLayout } from "./AuthLayout";

export default function Login() {
  const t = useT();
  const [mode, setMode] = useState<"magic" | "password">("magic");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "error" | "success"; text: string } | null>(null);
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") ?? "/";
  const { refresh } = useSession();

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      if (mode === "magic") {
        const r = await authClient.signIn.magicLink({ email, callbackURL: next });
        if (r.error) setMsg({ kind: "error", text: errText(t, r.error.code) });
        else setMsg({ kind: "success", text: t.magicSent });
      } else {
        const r = await authClient.signIn.email({ email, password });
        if (r.error) setMsg({ kind: "error", text: errText(t, r.error.code) });
        else {
          await refresh();
          nav(next, { replace: true });
        }
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title={t.signIn}>
      <Tabs
        value={mode}
        onChange={setMode}
        items={[
          { value: "magic", label: t.signInWithMagic },
          { value: "password", label: t.signInWithPassword },
        ]}
      />
      <form onSubmit={submit} className="space-y-4">
        <Field
          label={t.email}
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {mode === "password" ? (
          <Field
            label={t.password}
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        ) : null}
        {msg ? <Alert kind={msg.kind}>{msg.text}</Alert> : null}
        <Button type="submit" className="w-full" loading={busy}>
          {mode === "magic" ? t.sendMagic : t.signIn}
        </Button>
      </form>
      <div className="mt-5 flex flex-col gap-2 text-sm text-muted">
        {mode === "password" ? (
          <Link to="/recuperar" className="hover:text-text">
            {t.forgot}
          </Link>
        ) : null}
        <span>
          {t.noAccount}{" "}
          <Link to="/registro" className="font-semibold text-text">
            {t.createAccount}
          </Link>
        </span>
      </div>
    </AuthLayout>
  );
}
